import { FC, useState } from 'react';

import ReactTooltip from 'react-tooltip';
import { useAppSelector } from 'hooks/redux';
import { getText } from 'utils/stringUtil';
import {
  PR_RECOMMENDATION,
  PR_INFO,
  PR_PLAN_DETAILS,
  PR_HSA,
  PR_PREMIUM,
  PR_PLAN_NETWORK,
  PR_DEDUCTIBLE,
  PR_OUT_OF_POCKET_MAX,
  PR_OFFICE_VISIT,
  PR_SPECIALIST_VISIT,
  HOSPITAL_OUTPATIENT,
  HOSPITAL_INPATIENT,
  PR_RETAIL_TIER,
  PR_HSA_CONTRIBUTION_HRA_ALLOWANCE,
  PR_EST_ANNUAL_COST,
} from 'constants/languageConstants';
import { ReactComponent as RankIcon } from 'images/plan-recommender/icon-rank.svg';
import { ReactComponent as RankIconGreen } from 'images/plan-recommender/icon-rank-green.svg';
import { ReactComponent as RankIconStar } from 'images/plan-recommender/icon-rank-star.svg';
import { ReactComponent as RankIconStarGreen } from 'images/plan-recommender/icon-rank-star-green.svg';
import {
  DEFAULT_TEXT,
  getFrequencyName,
  PLAN_RECOMMENDER_PLAN_NOTES,
  RATE_TIERS,
  SUPER_SCRIPTS,
} from 'modules/planRecommender/pages/constants';
import {
  currencyFormatter,
  currencyFormatterForSignedNumbers,
} from 'utils/commonUtil';
import { getCardFieldValue } from 'utils/planRecommenderUtils/planRecommenderUtils';
import { PLAN_TYPE_HSA } from 'modules/benefitsWellness/constants/BenefitConstants';
import infoIcon from 'images/icons/icon-info.png';
import playButton from 'images/icons/play-button.png';
import metadata from 'data/metadata';
import PlanDetailsPopup from 'modules/planRecommender/components/PlanDetailsPopup/PlanDetailsPopup';
import PlanTypeInfoDrawer from 'modules/benefitsWellness/components/PlanTypeInfoDrawer/PlanTypeInfoDrawer';
import { useTableHeightSync } from 'modules/benefitsWellness/components/TableContext/TableContextProvider';

import styles from './PlanRecommenderPlanMobile.module.less';

type PlanRecommenderPlanProps = {
  plan: any;
  index: number;
  rank: number;
  activePlan: number;
  onClick: Function;
  isNTierPlan: boolean;
  dbgPlanFrequency: string;
  maxRxTiers: number;
};

const PlanRecommenderPlan: FC<PlanRecommenderPlanProps> = (
  props: PlanRecommenderPlanProps
) => {
  const {
    index,
    activePlan,
    plan,
    rank,
    isNTierPlan,
    onClick,
    dbgPlanFrequency,
    maxRxTiers,
  } = props;

  const [isOpenPlanDetails, setOpenPlanDetails] = useState<boolean>(false);
  const [isOpenPlanInfo, setOpenPlanInfo] = useState<boolean>(false);

  // Height synchronization hooks for consistent alignment across plan cards
  const { height: planNameHeight, ref: planNameRef } = useTableHeightSync(
    'planName',
    index
  );
  const { height: planNetworkHeight, ref: planNetworkRef } = useTableHeightSync(
    'planNetwork',
    index
  );
  const { height: deductibleHeight, ref: deductibleRef } = useTableHeightSync(
    'deductible',
    index
  );
  const { height: outOfPocketMaxHeight, ref: outOfPocketMaxRef } =
    useTableHeightSync('outOfPocketMax', index);
  const { height: officeVisitHeight, ref: officeVisitRef } = useTableHeightSync(
    'officeVisit',
    index
  );
  const { height: specialistVisitHeight, ref: specialistVisitRef } =
    useTableHeightSync('specialistVisit', index);
  const { height: hospitalInpatientHeight, ref: hospitalInpatientRef } =
    useTableHeightSync('hospitalInpatient', index);
  const { height: hospitalOutpatientHeight, ref: hospitalOutpatientRef } =
    useTableHeightSync('hospitalOutpatient', index);
  const { height: hsaHraHeight, ref: hsaHraRef } = useTableHeightSync(
    'hsaHra',
    index
  );
  const { height: annualCostHeight, ref: annualCostRef } = useTableHeightSync(
    'annualCost',
    index
  );

  // RX Tier height synchronization hooks (supporting up to 8 tiers)
  const { height: rxTier1Height, ref: rxTier1Ref } = useTableHeightSync(
    'rxTier1',
    index
  );
  const { height: rxTier2Height, ref: rxTier2Ref } = useTableHeightSync(
    'rxTier2',
    index
  );
  const { height: rxTier3Height, ref: rxTier3Ref } = useTableHeightSync(
    'rxTier3',
    index
  );
  const { height: rxTier4Height, ref: rxTier4Ref } = useTableHeightSync(
    'rxTier4',
    index
  );
  const { height: rxTier5Height, ref: rxTier5Ref } = useTableHeightSync(
    'rxTier5',
    index
  );
  const { height: rxTier6Height, ref: rxTier6Ref } = useTableHeightSync(
    'rxTier6',
    index
  );
  const { height: rxTier7Height, ref: rxTier7Ref } = useTableHeightSync(
    'rxTier7',
    index
  );
  const { height: rxTier8Height, ref: rxTier8Ref } = useTableHeightSync(
    'rxTier8',
    index
  );

  const language = useAppSelector((state) => state.benguide.data?.language);

  const togglePlanDetails = () => {
    setOpenPlanDetails(!isOpenPlanDetails);
  };

  const togglePlanInfo = () => {
    setOpenPlanInfo(!isOpenPlanInfo);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) {
      return (
        <div className={styles.rankWrapper}>
          {index !== activePlan ? (
            <RankIconStar className={styles.rankIcon} />
          ) : (
            <RankIconStarGreen className={styles.rankIcon} />
          )}
          <span>{getText(PR_RECOMMENDATION, language)}</span>
        </div>
      );
    } else {
      return (
        <div className={styles.rankWrapper}>
          {index !== activePlan ? (
            <RankIcon className={styles.rankIcon} />
          ) : (
            <RankIconGreen className={styles.rankIcon} />
          )}
          <div className={styles.rankText}>#{rank}</div>
        </div>
      );
    }
  };

  const filterRateTier = (plan: any) => {
    return RATE_TIERS.find((tier) => tier.fourTierValue === plan.rateTier);
  };

  const formatFieldValue = (
    key: any,
    isTooltip = false,
    hasDollar = true,
    shouldLowerCase = true
  ) => {
    const rate = filterRateTier(plan);
    const network = 'InNetwork';
    let value = getCardFieldValue(
      plan,
      key,
      network,
      rate,
      hasDollar,
      shouldLowerCase,
      dbgPlanFrequency
    );
    value =
      !value || value.toString().trim().length === 0
        ? 'defaultValue' in key
          ? key.defaultValue
          : DEFAULT_TEXT
        : value;
    return clearSuperScripts(value, !isTooltip);
  };

  const clearSuperScripts = (value: any, withElement = true) => {
    const fieldValue = SUPER_SCRIPTS.reduce(
      (current: any, superscript: any) => {
        const search = new RegExp(superscript.strValue, 'g');
        if (current.indexOf(superscript.strValue) >= 0) {
          return current.replace(search, '');
        } else {
          return current;
        }
      },
      value
    );
    if (withElement) {
      return <>{fieldValue}</>;
    } else {
      return fieldValue;
    }
  };

  const { planRecommenderPlanKeys: keys } = metadata;

  const {
    benefitCarrier,
    name,
    estAnnualBreakdown = {},
    type: planType,
  } = plan;
  const {
    estAnnualCost = 0,
    hsaContribution = 0,
    hraContribution = 0,
  } = estAnnualBreakdown;
  const { type, hsaCompatible } = plan;
  const rateTier = filterRateTier(plan) as any;

  const planClasses = [styles.plan];
  if (index === activePlan) {
    planClasses.push(styles.activePlan);
  }

  const getRxTiers = (divCount: number) => {
    const rxColumns = [];
    const rxTierRefs = [
      rxTier1Ref,
      rxTier2Ref,
      rxTier3Ref,
      rxTier4Ref,
      rxTier5Ref,
      rxTier6Ref,
      rxTier7Ref,
      rxTier8Ref,
    ];
    const rxTierHeights = [
      rxTier1Height,
      rxTier2Height,
      rxTier3Height,
      rxTier4Height,
      rxTier5Height,
      rxTier6Height,
      rxTier7Height,
      rxTier8Height,
    ];

    for (let i = 8; i <= divCount + 7; i++) {
      const tierNumber = i - 7;
      const tierIndex = tierNumber - 1; // Convert to 0-based index for arrays
      const tierRef = rxTierRefs[tierIndex];
      const tierHeight = rxTierHeights[tierIndex];

      rxColumns.push(
        <div
          key={`rxTier-${i}`}
          className={
            i % 2
              ? `${styles.valueField} ${styles.darkBg}`
              : `${styles.valueField}`
          }
          ref={tierRef}
          style={{ height: tierHeight }}
          title={formatFieldValue(keys[i], true)}
        >
          <div>
            {getText(PR_RETAIL_TIER, language)} {i - 7}
          </div>
          {formatFieldValue(keys[i])}
        </div>
      );
    }
    return <>{rxColumns}</>;
  };

  return (
    <div
      className={planClasses.join(' ')}
      onClick={() => {
        onClick(index);
      }}
    >
      {getRankIcon(rank)}
      <div className={`${isNTierPlan ? styles.noPremium : ''} ${styles.upper}`}>
        <div className={styles.headerBorder}></div>
        <img
          className={styles.carrierLogo}
          src={benefitCarrier?.logoUrl}
          alt=""
        />
        <div
          className={styles.planName}
          ref={planNameRef}
          style={{ height: planNameHeight }}
        >
          <div className={styles.planNameContent} title={name}>
            {name}
          </div>
          <img
            src={infoIcon}
            alt="info"
            className={styles.infoTooltipIcon}
            data-tip={
              hsaCompatible
                ? PLAN_RECOMMENDER_PLAN_NOTES[PLAN_TYPE_HSA]
                : PLAN_RECOMMENDER_PLAN_NOTES[type]
            }
          />
          <ReactTooltip
            className={styles.infoTooltip}
            place="right"
            type="light"
            effect="solid"
            border={true}
            borderColor="gray"
          />
        </div>
        <div className={styles.links}>
          {type && (
            <div className={styles.infoLink} onClick={togglePlanInfo}>
              <img src={playButton} alt="play" />
              {hsaCompatible ? getText(PR_HSA, language) : planType}{' '}
              {getText(PR_INFO, language)}
            </div>
          )}
          <div className={styles.detailsLink} onClick={togglePlanDetails}>
            {getText(PR_PLAN_DETAILS, language)}
          </div>
        </div>
        {!isNTierPlan && (
          <div className={`${styles.valueField} ${styles.monthlyPremium}`}>
            {`${getFrequencyName(dbgPlanFrequency)} ${getText(
              PR_PREMIUM,
              language
            )}`}
            <div
              className={styles.value}
              title={formatFieldValue(keys[0], true)}
            >
              {formatFieldValue(keys[0])}
            </div>
            <div className={styles.cover}>{rateTier.coverageLabel}</div>
          </div>
        )}
        <div
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[1], true)}
          ref={planNetworkRef}
          style={{ height: planNetworkHeight }}
        >
          <div>{getText(PR_PLAN_NETWORK, language)}</div>
          {formatFieldValue(keys[1], false, false, false)}
        </div>
        <div
          className={styles.valueField}
          title={formatFieldValue(keys[2], true)}
          ref={deductibleRef}
          style={{ height: deductibleHeight }}
        >
          <div>{getText(PR_DEDUCTIBLE, language)}</div>
          {formatFieldValue(keys[2])}
        </div>
        <div
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[3], true)}
          ref={outOfPocketMaxRef}
          style={{ height: outOfPocketMaxHeight }}
        >
          <div>{getText(PR_OUT_OF_POCKET_MAX, language)}</div>
          {formatFieldValue(keys[3])}
        </div>
        <div
          className={styles.valueField}
          title={formatFieldValue(keys[4], true)}
          ref={officeVisitRef}
          style={{ height: officeVisitHeight }}
        >
          <div>{getText(PR_OFFICE_VISIT, language)}</div>
          {formatFieldValue(keys[4])}
        </div>
        <div
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[5], true)}
          ref={specialistVisitRef}
          style={{ height: specialistVisitHeight }}
        >
          <div>{getText(PR_SPECIALIST_VISIT, language)}</div>
          {formatFieldValue(keys[5])}
        </div>

        <div
          className={styles.valueField}
          title={formatFieldValue(keys[6], true)}
          ref={hospitalInpatientRef}
          style={{ height: hospitalInpatientHeight }}
        >
          <div>{getText(HOSPITAL_INPATIENT, language)}</div>
          {formatFieldValue(keys[6])}
        </div>
        <div
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[7], true)}
          ref={hospitalOutpatientRef}
          style={{ height: hospitalOutpatientHeight }}
        >
          <div>{getText(HOSPITAL_OUTPATIENT, language)}</div>
          {formatFieldValue(keys[7])}
        </div>
        {getRxTiers(maxRxTiers)}
        <div
          className={
            maxRxTiers % 2
              ? `${styles.valueField} ${styles.darkBg} ${styles.hsaHraField} `
              : `${styles.valueField} ${styles.hsaHraField} `
          }
          title={currencyFormatter(hsaContribution)}
          ref={hsaHraRef}
          style={{ height: hsaHraHeight }}
        >
          <div>{getText(PR_HSA_CONTRIBUTION_HRA_ALLOWANCE, language)}</div>
          {currencyFormatter(hsaContribution)} /
          {currencyFormatter(hraContribution)}
        </div>

        <div
          className={`${styles.valueField} ${styles.annualCost}`}
          title={currencyFormatterForSignedNumbers(estAnnualCost)}
          ref={annualCostRef}
          style={{ height: annualCostHeight }}
        >
          <div className={styles.EstCost}>
            {getText(PR_EST_ANNUAL_COST, language)}
          </div>
          {currencyFormatterForSignedNumbers(estAnnualCost)}
        </div>
      </div>
      <div className={styles.planFooter}></div>
      <PlanTypeInfoDrawer
        isDrawerOpen={isOpenPlanInfo}
        onClose={() => setOpenPlanInfo(false)}
        isHSA={plan.hsaCompatible}
        planType={plan.type}
      />
      <PlanDetailsPopup
        isdrawerOpen={isOpenPlanDetails}
        onHide={togglePlanDetails}
        plan={plan}
        planId={plan.id}
        selectedRate={rateTier}
        networkType={'InNetwork'}
        planType={'medical'}
      />
    </div>
  );
};

export default PlanRecommenderPlan;
